<template>
  <b-container class="user-list" fluid>
    <b-row class="d-flex user" v-for="item in userList" :key="item.user.id">
      <div class="my-2 overflow-hidden user-row" no-gutters>
        <profile-icon
          class="icon"
          cols="1"
          :userName="item.user.name"
          :id="item.user.directId"
          :image="item.user.getProfileIconUrl()"
          :size="25"
        />
        <div class="user-label" :class="{ 'hide-reaction-date': !showReactionDate }">
          {{ item.user.name }}
        </div>
        <div v-if="showReactionDate" class="reaction-date">{{ formatDate(item.updatedAt) }}</div>
      </div>
    </b-row>
  </b-container>
</template>

<style lang="scss" scoped>
.user-list {
  max-height: 80vh;
  padding: 0;
}
.user {
  width: 100%;
  margin: 0;
  padding: 0 8px;
  border-bottom: 1px solid #c9c9c9;
}
.user-row {
  display: grid;
  grid-template-columns: auto 1fr 1fr;
  grid-template-rows: auto auto;
  gap: 0 8px;
  align-items: center;
  width: 100%;
}
.icon {
  grid-row: 1 / span 2;
  grid-column: 1;
  display: flex;
  align-items: center;
}
.user-label {
  grid-row: 2;
  grid-column: 2 / span 3;
  max-width: 100%;
  font-size: 13px;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  flex-grow: 1;
  &.hide-reaction-date {
    grid-row: 1 / span 2;
    grid-column: 2 / span 3;
  }
}
.reaction-date {
  grid-row: 1;
  grid-column: 2 / span 3;
  min-width: 165px;
  font-size: 11px;
  color: #747474;
}

// PC用画面
@media (min-width: 768px) {
  .user-row {
    grid-template-columns: auto 1fr 180px;
  }
  .user-label {
    grid-row: 1 / span 2;
    grid-column: 2;
  }
  .reaction-date {
    grid-row: 1 / span 2;
    grid-column: 3;
    font-size: 13px;
    color: inherit;
  }
}
</style>

<script lang="ts">
import { computed, defineComponent, type PropType } from "vue";
import { useAppStore, useDomainStore, useUserStore } from "@/store";
import type { FavoriteModalUser } from "../model";
import { User } from "../model";
import ProfileIcon from "./ProfileIcon.vue";
import type { ShowReactionDateType } from "@/support-acl-types";
import moment from "moment";

/** moment のロケール設定に依存せず日本語表記にするための曜日ラベル（moment#day() の 0=日曜 に対応） */
const DAY_OF_WEEK_LABELS = ["日", "月", "火", "水", "木", "金", "土"];

export default defineComponent({
  name: "user-list",
  components: {
    ProfileIcon,
  },
  props: {
    userList: {
      type: Array as PropType<FavoriteModalUser[]>,
      default: () => [],
    },
  },
  setup() {
    const showReactionDate = computed((): boolean => {
      const showReactionDatePolicy = useDomainStore().getShowReactionDatePolicy as boolean;
      if (showReactionDatePolicy) {
        const domainId = useAppStore().domainId as string;
        const me = useUserStore().me as User;
        const showReactionDateType = useDomainStore()
          .getShowReactionDateType as ShowReactionDateType;
        switch (showReactionDateType) {
          case "owner":
            return me.isOwnerRole(domainId);
          case "admin":
            return me.isOwnerRole(domainId) || me.isAdminRole(domainId);
          case "user":
            // Role が ゲストの場合は表示しない
            return me.isOwnerRole(domainId) || me.isAdminRole(domainId) || me.isUserRole(domainId);
          case "all":
            return true;
        }
      }
      return false;
    });

    const formatDate = (updatedAt: string): string => {
      if (!updatedAt) return "";
      const dt = moment(updatedAt);
      // 曜日はリテラルとして埋め込む（[] 内は moment のフォーマットトークンとして解釈されない）
      return dt.format(`YYYY/MM/DD[(${DAY_OF_WEEK_LABELS[dt.day()]})]HH:mm`);
    };

    return {
      showReactionDate,
      formatDate,
    };
  },
});
</script>
